import { lazy, type ComponentType } from "react";

/**
 * Wraps React.lazy() with auto-retry + one-shot reload on stale chunk errors.
 *
 * After a deploy, browsers may still hold references to old chunk filenames
 * that no longer exist on the CDN. Vite/Rollup throws:
 *   - "Importing a module script failed."
 *   - "Failed to fetch dynamically imported module"
 *   - "error loading dynamically imported module"
 *
 * Strategy:
 *  1. Retry the import once (handles transient network blips).
 *  2. If it still fails, force a single full page reload (sessionStorage flag
 *     prevents an infinite reload loop).
 */
const RELOAD_FLAG = "lovable:chunk-reload-attempted";

function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? "");
  return (
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Successful load — clear the reload guard.
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* ignore */
      }
      return mod;
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;

      // One quick retry for transient failures.
      try {
        const mod = await factory();
        try {
          sessionStorage.removeItem(RELOAD_FLAG);
        } catch {
          /* ignore */
        }
        return mod;
      } catch (err2) {
        if (!isChunkLoadError(err2)) throw err2;

        // Force a hard reload — but only once per session to avoid loops.
        let alreadyReloaded = false;
        try {
          alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === "1";
          if (!alreadyReloaded) sessionStorage.setItem(RELOAD_FLAG, "1");
        } catch {
          /* ignore */
        }

        if (!alreadyReloaded && typeof window !== "undefined") {
          console.warn(
            "[lazyWithRetry] Stale chunk detected, reloading page once.",
            err2,
          );
          window.location.reload();
          // Return a never-resolving promise so React stays in Suspense
          // until the reload kicks in.
          return new Promise<{ default: T }>(() => {});
        }

        throw err2;
      }
    }
  });
}
